import { useEffect, useRef, useState } from "react";
import { X, ExternalLink, HeartHandshake, CalendarDays } from "lucide-react";
import { Link } from "react-router-dom";
import * as Dialog from "@radix-ui/react-dialog";
import { PRAYER_CONFERENCE_ZOOM } from "@/components/MinistryRibbon";
import { GENERAL_BODY_MEETING, isGeneralBodyMeetingUpcoming } from "@/lib/generalBodyMeeting";

/** Homepage flash cards: retain monthly prayer and promote the upcoming meeting. */
const ConferenceFlashCardsModal = () => {
  const [isOpen, setIsOpen] = useState(false);
  const previousFocus = useRef<HTMLElement | null>(null);
  const showMeeting = isGeneralBodyMeetingUpcoming();

  useEffect(() => {
    // Show on each homepage mount without reopening when a Zoom tab returns focus.
    const timer = window.setTimeout(() => {
      previousFocus.current = document.activeElement as HTMLElement | null;
      setIsOpen(true);
    }, 350);
    return () => window.clearTimeout(timer);
  }, []);

  return (
    <Dialog.Root open={isOpen} onOpenChange={setIsOpen}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[10000] bg-black/70 backdrop-blur-sm" />
        <Dialog.Content aria-modal="true" onCloseAutoFocus={(event) => {
          event.preventDefault();
          previousFocus.current?.focus();
        }} className="fixed left-1/2 top-1/2 z-[10001] w-[calc(100%-1.5rem)] max-w-3xl max-h-[85dvh] -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-2xl bg-card text-card-foreground shadow-2xl">
          <div className="sticky top-0 z-10 bg-primary px-5 py-4 pr-16 text-primary-foreground sm:px-8 sm:pr-16">
            <p className="text-xs font-semibold uppercase tracking-widest">OFUESI</p>
            <Dialog.Title className="text-lg font-bold sm:text-2xl">Meetings & Prayer</Dialog.Title>
            <Dialog.Description className="mt-1 text-sm text-primary-foreground">
              Join our OFUESI family gatherings on Zoom.
            </Dialog.Description>
            <Dialog.Close aria-label="Close meeting announcements" className="absolute right-2 top-2 flex h-11 w-11 items-center justify-center rounded-lg hover:bg-black/20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-white">
              <X className="h-5 w-5" aria-hidden="true" />
            </Dialog.Close>
          </div>
          <div className="p-4 sm:p-6">
            <div className={showMeeting ? "grid grid-cols-1 gap-4 sm:grid-cols-2" : "grid grid-cols-1 gap-4"}>
              {showMeeting && (
                <div className="rounded-xl border-2 border-indigo-700 bg-indigo-900 p-5 text-white shadow-sm">
                  <a href={GENERAL_BODY_MEETING.zoomUrl} target="_blank" rel="noopener noreferrer"
                    aria-label={`${GENERAL_BODY_MEETING.title} flyer — join on Zoom (opens in a new tab)`}
                    className="block overflow-hidden rounded-lg focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white">
                    <img src={GENERAL_BODY_MEETING.flyerImageUrl} alt={GENERAL_BODY_MEETING.flyerAlt}
                      className="mx-auto h-auto max-h-[55dvh] w-auto max-w-full rounded-lg object-contain"
                      loading="lazy" decoding="async" />
                  </a>
                  <h3 className="mt-4 flex items-center gap-2 text-xl font-bold">
                    <CalendarDays className="h-6 w-6 shrink-0" aria-hidden="true" />
                    {GENERAL_BODY_MEETING.title}
                  </h3>
                  <p className="mt-1 text-sm text-indigo-100">
                    {GENERAL_BODY_MEETING.theme} · Speaker: {GENERAL_BODY_MEETING.speaker}
                  </p>
                  <time dateTime={GENERAL_BODY_MEETING.dateTime} className="mt-2 block text-sm">
                    {GENERAL_BODY_MEETING.dateLabel}<br />{GENERAL_BODY_MEETING.timeLabel}
                  </time>
                  <div className="mt-4 flex flex-wrap items-center gap-3">
                    <a href={GENERAL_BODY_MEETING.zoomUrl} target="_blank" rel="noopener noreferrer"
                      className="inline-flex min-h-11 items-center gap-2 rounded-lg bg-white px-3 py-2 text-sm font-bold text-indigo-900 hover:bg-indigo-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white">
                      <ExternalLink className="h-4 w-4" aria-hidden="true" /> Join on Zoom
                      <span className="sr-only"> (opens in a new tab)</span>
                    </a>
                    <a href={GENERAL_BODY_MEETING.flyerPdfUrl} target="_blank" rel="noopener noreferrer"
                      className="inline-flex min-h-11 items-center gap-2 rounded-lg border border-white/60 px-3 py-2 text-sm font-semibold text-white hover:bg-white/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white">
                      Download flyer (PDF)
                      <span className="sr-only"> (opens in a new tab)</span>
                    </a>
                  </div>
                </div>
              )}
              <a href={PRAYER_CONFERENCE_ZOOM} target="_blank" rel="noopener noreferrer"
                className="rounded-xl border-2 border-sky-700 bg-sky-800 p-5 text-white shadow-sm hover:bg-sky-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-700">
                <HeartHandshake className="mb-3 h-6 w-6" aria-hidden="true" />
                <h3 className="text-xl font-bold">Monthly Prayer Conference</h3>
                <p className="mt-3 text-sm">Every 1st Saturday · 10:30–11:30 AM EST · on Zoom</p>
                <span className="mt-4 inline-flex min-h-11 items-center gap-2 rounded-lg bg-white px-3 py-2 text-sm font-bold text-sky-800">
                  <ExternalLink className="h-4 w-4" aria-hidden="true" /> Join now
                </span>
                <span className="sr-only"> (opens in a new tab)</span>
              </a>
            </div>
            <div className="mt-5 flex justify-center">
              <Link to="/conference" onClick={() => setIsOpen(false)} className="inline-flex min-h-11 items-center rounded px-3 text-sm font-semibold text-primary hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary">
                View the full Conference Hub →
              </Link>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default ConferenceFlashCardsModal;
